import React from "react";
import PropTypes from "prop-types";
import Skeleton from "react-loading-skeleton";

import DetailOverviewRow from "../../../components/DetailOverviewRow";
import DateFormatter from "../../../components/DateFormatter";
import DiscountCodeTypePill from "../components/DiscountCodeTypePill";
import PriceFormatter from "../../../components/priceFormatter";

// eslint-disable-next-line complexity
function DiscountCodeDetailOverview({ discountCode }) {
    return (
        <React.Fragment>
            <div className="card mb-3">
                <div className="card-header">
                    <b>Kortingscode</b>
                </div>
                <div className="card-body">
                    <table className="table table-borderless mb-0">
                        <tbody>
                        <DetailOverviewRow title="Code">
                            { discountCode ? (
                                discountCode.code
                            ) : (
                                <Skeleton width={150}/>
                            )}
                        </DetailOverviewRow>
                        <DetailOverviewRow title="Type">
                            { discountCode ? (
                                <DiscountCodeTypePill type={ discountCode.type }/>
                            ) : (
                                <Skeleton width={100}/>
                            )}
                        </DetailOverviewRow>
                        <DetailOverviewRow title="Ingeschakeld">
                            { discountCode ? (
                                discountCode.enabled ? (
                                    <i className="fas fa-check text-success"/>
                                ) : (
                                    <i className="fas fa-times text-danger"/>
                                )
                            ) : (
                                <Skeleton width={30}/>
                            )}
                        </DetailOverviewRow>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-header">
                    <b>Korting</b>
                </div>
                <div className="card-body">
                    <table className="table table-borderless mb-0">
                        <tbody>
                        { (!discountCode || discountCode.discountValue !== null) && (
                            <DetailOverviewRow title="Kortingsbedrag">
                                { discountCode ? (
                                    <PriceFormatter price={ discountCode.discountValue }/>
                                ) : (
                                    <Skeleton width={75}/>
                                )}
                            </DetailOverviewRow>
                        )}
                        { (!discountCode || discountCode.discountPercentage !== null) && (
                            <DetailOverviewRow title="Kortingspercentage">
                                { discountCode ? (
                                    Math.round(discountCode.discountPercentage * 100) + "%"
                                ) : (
                                    <Skeleton width={75}/>
                                )}
                            </DetailOverviewRow>
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-header">
                    <b>Gebruik</b>
                </div>
                <div className="card-body">
                    <table className="table table-borderless mb-0">
                        <tbody>
                        <DetailOverviewRow title="Gebruik">
                            { discountCode ? (
                                discountCode.usageCount + " / " + (discountCode.maxUsageValue !== null ? discountCode.maxUsageValue : "Onbeperkt")
                            ) : (
                                <Skeleton width={100}/>
                            )}
                        </DetailOverviewRow>
                        <DetailOverviewRow title="Totaal kortingsbedrag gebruikt">
                            { discountCode ? (
                                <PriceFormatter price={ discountCode.discountValueUsed }/>
                            ) : (
                                <Skeleton width={75}/>
                            )}
                        </DetailOverviewRow>
                        <DetailOverviewRow title="Combineerbaar">
                            { discountCode ? (
                                discountCode.combinable ? (
                                    <i className="fas fa-check text-success"/>
                                ) : (
                                    <i className="fas fa-times text-danger"/>
                                )
                            ) : (
                                <Skeleton width={30}/>
                            )}
                        </DetailOverviewRow>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-header">
                    <b>Geldigheidsperiode</b>
                </div>
                <div className="card-body">
                    <table className="table table-borderless mb-0">
                        <tbody>
                        <DetailOverviewRow title="Startdatum">
                            { discountCode ? (
                                discountCode.startDate ? (
                                    <DateFormatter date={ discountCode.startDate }/>
                                ) : "-"
                            ) : (
                                <Skeleton width={120}/>
                            )}
                        </DetailOverviewRow>
                        <DetailOverviewRow title="Verloopdatum">
                            { discountCode ? (
                                discountCode.expirationDate ? (
                                    <DateFormatter date={ discountCode.expirationDate }/>
                                ) : "-"
                            ) : (
                                <Skeleton width={120}/>
                            )}
                        </DetailOverviewRow>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-header">
                    <b>Producten</b>
                </div>
                <div className="card-body">
                    <table className="table table-borderless mb-0">
                        <tbody>
                        <DetailOverviewRow title="Alle producten geldig">
                            { discountCode ? (
                                discountCode.allProductsEligible ? (
                                    <i className="fas fa-check text-success"/>
                                ) : (
                                    <i className="fas fa-times text-danger"/>
                                )
                            ) : (
                                <Skeleton width={30}/>
                            )}
                        </DetailOverviewRow>
                        { discountCode && !discountCode.allProductsEligible && (
                            <DetailOverviewRow title="Geldige producten">
                                { discountCode.eligibleProducts && discountCode.eligibleProducts.length > 0
                                    ? discountCode.eligibleProducts.map(product => product.name).join(", ")
                                    : "-"
                                }
                            </DetailOverviewRow>
                        )}
                        { discountCode && discountCode.requiredProductsAll !== null && (
                            <DetailOverviewRow title="Vereiste producten modus">
                                { discountCode.requiredProductsAll ? "Alle vereiste producten" : "Minstens een vereist product" }
                            </DetailOverviewRow>
                        )}
                        { discountCode && discountCode.requiredProducts && discountCode.requiredProducts.length > 0 && (
                            <DetailOverviewRow title="Vereiste producten">
                                { discountCode.requiredProducts.map(product => product.name).join(", ") }
                            </DetailOverviewRow>
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-header">
                    <b>Details</b>
                </div>
                <div className="card-body">
                    <table className="table table-borderless mb-0">
                        <tbody>
                        <DetailOverviewRow title="Aangemaakt op">
                            { discountCode ? (
                                discountCode.creationDate ? (
                                    <DateFormatter date={ discountCode.creationDate }/>
                                ) : "-"
                            ) : (
                                <Skeleton width={120}/>
                            )}
                        </DetailOverviewRow>
                        <DetailOverviewRow title="Aangemaakt door">
                            { discountCode ? (
                                discountCode.author ? discountCode.author.name : "-"
                            ) : (
                                <Skeleton width={100}/>
                            )}
                        </DetailOverviewRow>
                        </tbody>
                    </table>
                </div>
            </div>
        </React.Fragment>
    )
}

DiscountCodeDetailOverview.propTypes = {
    discountCode: PropTypes.object
}

export default React.memo(DiscountCodeDetailOverview);
